import clsx from 'clsx';
import { Activity, Briefcase, CheckCircle2, ChevronRight, Server, ServerCrash } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useI18n } from '../lib/i18n';
import { formatDate, formatDateTime, formatNumber } from '../lib/format';
import {
  checkCodeText,
  healthLabel,
  healthTone,
  outageText,
  severityLabel,
  severityTone,
  type MonitoringOverview,
  type ServerHealth,
} from '../lib/monitoring';
import { MonitorBanner } from '../components/MonitorBanner';
import { MonitoringTiles, useMonitoringOverview } from '../components/MonitoringTiles';
import { UrlCountsText } from './MonitoringServers';
import { IncidentsTable } from './MonitoringUrls';
import {
  Badge,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingBlock,
  PageHeader,
  StatusDot,
  Table,
  Td,
  Th,
  Tr,
} from '../components/ui';

/**
 * Monitoring › Overview (operator's spec, 2026-10-20): every server and URL at a
 * glance — how many are up and down, what is down right now and since when,
 * uptime, the latest outages and the certificates running out. Read again every
 * 30 seconds; the monitor checks every minute.
 *
 * What is down and the servers sit one under the other, each the page's full
 * width (operator, 2026-10-20: "put under each other in new design to be more
 * easy to view").
 */
export function MonitoringOverviewPage() {
  const { t, locale } = useI18n();
  const { data, isLoading, error, refetch, isFetching } = useMonitoringOverview();

  if (isLoading) return <LoadingBlock />;
  if (error || !data) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    // Held as it was while it reads again: no flash, no jump.
    <div className={isFetching ? 'opacity-90 transition-opacity' : 'transition-opacity'}>
      <PageHeader title={t('monitoring.overview.title')} subtitle={t('monitoring.overview.subtitle')} />
      <MonitorBanner />

      <MonitoringTiles data={data} className="mb-5" />

      <DownNow incidents={data.openIncidents} />
      <ServerTiles servers={data.serverCards} />

      <Card padded={false} className="mb-5">
        <div className="p-5 pb-0">
          <CardHeader title={t('monitoring.overview.recent')} subtitle={t('monitoring.url.incidentsHint')} />
        </div>
        {data.recentIncidents.length === 0 ? (
          <p className="px-5 pb-5 text-body-sm text-muted">{t('monitoring.url.noIncidents')}</p>
        ) : (
          <IncidentsTable incidents={data.recentIncidents} showUrl />
        )}
      </Card>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader title={t('monitoring.overview.certificatesList')} subtitle={t('monitoring.overview.certificatesListHint')} />
          {data.certificates.length === 0 ? (
            <p className="text-body-sm text-muted">{t('monitoring.overview.noCertificates')}</p>
          ) : (
            <ul className="divide-y hairline">
              {data.certificates.map((c) => (
                <li key={c.url.id} className="flex items-center justify-between gap-3 py-2">
                  <Link to={`/monitoring/urls/${c.url.id}`} className="min-w-0 hover:text-primary-600 dark:hover:text-primary-400">
                    <span className="block truncate text-body-sm font-medium">{c.url.name}</span>
                    <span className="block text-caption text-muted">{c.server.name} · {formatDate(c.sslExpiresAt, locale)}</span>
                  </Link>
                  <Badge tone={c.daysLeft <= c.warnDays ? (c.daysLeft <= 3 ? 'error' : 'warning') : 'success'}>
                    {t('monitoring.url.daysLeft', { n: Math.max(0, c.daysLeft) })}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card>
          <CardHeader title={t('monitoring.overview.slowest')} subtitle={t('monitoring.overview.slowestHint')} />
          {data.slowest.length === 0 ? (
            <p className="text-body-sm text-muted">{t('monitoring.url.noChecks')}</p>
          ) : (
            <ul className="divide-y hairline">
              {data.slowest.map((u) => (
                <li key={u.id} className="flex items-center justify-between gap-3 py-2">
                  <Link to={`/monitoring/urls/${u.id}`} className="min-w-0 hover:text-primary-600 dark:hover:text-primary-400">
                    <span className="block truncate text-body-sm font-medium">{u.name}</span>
                    <span className="block text-caption text-muted">{u.server.name}</span>
                  </Link>
                  <span className="tabular text-body-sm">{u.lastResponseMs === null ? '—' : t('monitoring.url.ms', { n: formatNumber(u.lastResponseMs, locale) })}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

/**
 * What is down right now, longest first: a row a URL — its server and project,
 * how much it matters, why it is down, since when and for how long. A row opens
 * its URL; the server's name opens the server. With nothing down, one quiet
 * line instead of a tall empty card.
 */
function DownNow({ incidents }: { incidents: MonitoringOverview['openIncidents'] }) {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const count = incidents.length;

  return (
    <Card padded={false} className="mb-5">
      <div className="p-5 pb-0">
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              {t('monitoring.overview.downNow')}
              {count > 0 && (
                <Badge tone="error" className="tabular">
                  {formatNumber(count, locale)}
                </Badge>
              )}
            </span>
          }
          subtitle={count > 0 ? t('monitoring.overview.downNowSubtitle') : undefined}
          action={
            <Link to="/monitoring/incidents" className="text-body-sm text-muted hover:text-[var(--text-primary)]">
              {t('monitoring.overview.allIncidents')}
            </Link>
          }
        />
      </div>
      {count === 0 ? (
        <div className="mx-5 mb-5 flex items-start gap-3 rounded-md bg-success-bg px-4 py-3 ring-1 ring-inset ring-success/30 dark:bg-success/12">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success-fg dark:text-success" aria-hidden />
          <span className="min-w-0">
            <span className="block font-medium text-success-fg dark:text-success">{t('monitoring.overview.nothingDown')}</span>
            <span className="block text-body-sm text-muted">{t('monitoring.overview.nothingDownHint')}</span>
          </span>
        </div>
      ) : (
        <>
          {/* A phone or a tablet: one block a URL, how long it has been down at its end. */}
          <ul className="divide-y hairline border-t hairline lg:hidden">
            {incidents.map((incident) => (
              <li key={incident.id}>
                <Link
                  to={`/monitoring/urls/${incident.url.id}`}
                  className="block px-5 py-3 transition-colors hover:bg-[var(--surface-sunken)]"
                >
                  <span className="flex items-start justify-between gap-3">
                    <span className="flex min-w-0 items-center gap-2">
                      <StatusDot tone="alert" />
                      <span className="truncate font-medium text-[var(--text-primary)]">{incident.url.name}</span>
                    </span>
                    <span className="shrink-0 font-semibold tabular text-error-fg dark:text-error-dark">
                      {outageText(incident.durationSeconds, locale)}
                    </span>
                  </span>
                  <span
                    dir="ltr"
                    className="mt-0.5 block truncate text-start font-mono text-mono-sm text-[var(--text-tertiary)] rtl:text-end"
                  >
                    {incident.url.url}
                  </span>
                  <span className="mt-2 flex flex-wrap items-center gap-1.5 text-body-sm text-[var(--text-secondary)]">
                    <Badge tone={severityTone(incident.url.severity)}>{t(severityLabel(incident.url.severity))}</Badge>
                    <span>{incident.url.server.name}</span>
                    {incident.url.project && (
                      <Badge tone="brand" title={localized(incident.url.project)}>
                        {incident.url.project.code}
                      </Badge>
                    )}
                  </span>
                  <span className="mt-1 block text-caption text-muted">
                    {checkCodeText(incident.causeCode, incident.causeDetail, t)} · {t('monitoring.overview.since')}{' '}
                    <span className="tabular">{formatDateTime(incident.startedAt, locale)}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          {/* A wider screen: a table, the columns lined up to read down. */}
          <div className="hidden lg:block">
            <Table>
              <thead>
                <tr>
                  <Th>{t('monitoring.url.url')}</Th>
                  <Th>{t('monitoring.url.server')}</Th>
                  <Th>{t('monitoring.url.severity')}</Th>
                  <Th>{t('monitoring.url.cause')}</Th>
                  <Th className="hidden whitespace-nowrap xl:table-cell">{t('monitoring.overview.since')}</Th>
                  <Th align="end" className="whitespace-nowrap">
                    {t('monitoring.overview.downFor')}
                  </Th>
                </tr>
              </thead>
              <tbody>
                {incidents.map((incident) => (
                  <Tr key={incident.id} onClick={() => navigate(`/monitoring/urls/${incident.url.id}`)}>
                    <Td>
                      <span className="flex items-center gap-2">
                        <StatusDot tone="alert" />
                        <Link
                          to={`/monitoring/urls/${incident.url.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="font-medium hover:text-primary-600 dark:hover:text-primary-400"
                        >
                          {incident.url.name}
                        </Link>
                      </span>
                      <span
                        dir="ltr"
                        className="mt-0.5 block max-w-[24rem] truncate text-start font-mono text-mono-sm text-[var(--text-tertiary)] rtl:text-end"
                      >
                        {incident.url.url}
                      </span>
                    </Td>
                    <Td>
                      <span className="flex flex-wrap items-center gap-1.5">
                        <Link
                          to={`/monitoring/servers/${incident.url.server.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="hover:text-primary-600 dark:hover:text-primary-400"
                        >
                          {incident.url.server.name}
                        </Link>
                        {incident.url.project && (
                          <Badge tone="brand" title={localized(incident.url.project)}>
                            {incident.url.project.code}
                          </Badge>
                        )}
                      </span>
                    </Td>
                    <Td>
                      <Badge tone={severityTone(incident.url.severity)}>{t(severityLabel(incident.url.severity))}</Badge>
                    </Td>
                    <Td>
                      <span className="text-[var(--text-secondary)]">{checkCodeText(incident.causeCode, incident.causeDetail, t)}</span>
                    </Td>
                    <Td className="hidden whitespace-nowrap tabular xl:table-cell">{formatDateTime(incident.startedAt, locale)}</Td>
                    <Td align="end" className="whitespace-nowrap">
                      <span className="block font-semibold tabular text-error-fg dark:text-error-dark">
                        {outageText(incident.durationSeconds, locale)}
                      </span>
                      {/* Narrower than xl the "since" column is hidden: its time comes under the duration. */}
                      <span className="block text-caption text-faint xl:hidden">{formatDateTime(incident.startedAt, locale)}</span>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </Card>
  );
}

/**
 * A server card's edge and fill by its health. A server that is fine stays
 * plain, so the ones that need a look stand out: amber-tinted with a warning,
 * red-tinted and glowing when offline. A dashed edge is a server nothing checks
 * yet — no URL on it, or every one paused.
 */
const CARD_TONE: Record<ServerHealth, string> = {
  ONLINE: 'hairline shadow-xs hover:border-[var(--line-strong)] hover:shadow-md dark:hover:shadow-edge-neutral',
  PENDING: 'hairline shadow-xs hover:border-[var(--line-strong)] hover:shadow-md dark:hover:shadow-edge-neutral',
  WARNING:
    'border-warning/45 bg-warning-bg/60 shadow-xs hover:border-warning/70 hover:shadow-md dark:bg-warning/6 dark:hover:shadow-edge-neutral',
  OFFLINE:
    'border-error/50 bg-error-bg/60 shadow-md shadow-error/12 hover:border-error/70 hover:shadow-lg hover:shadow-error/20 dark:bg-error/8 dark:shadow-error/20',
  NOT_MONITORED: 'border-dashed border-[var(--line-strong)] hover:shadow-md dark:hover:shadow-edge-neutral',
};

/** The server icon's tile, in its health's tones — solid red when the server is offline. */
const ICON_TONE: Record<ServerHealth, string> = {
  ONLINE: 'bg-success-bg text-success-fg ring-success/30 dark:bg-success/12 dark:text-success',
  WARNING: 'bg-warning-bg text-warning-fg ring-warning/35 dark:bg-warning/14 dark:text-warning-dark',
  OFFLINE: 'bg-error-fill text-white ring-error-fill',
  PENDING: 'bg-info-bg text-info-fg ring-info/30 dark:bg-info/12 dark:text-info-dark',
  NOT_MONITORED: 'bg-[var(--surface-sunken)] text-[var(--text-tertiary)] ring-neutral-500/20',
};

/**
 * Every server as a card (operator, 2026-10-20: "add servers icon no need for
 * progress make more attractive"): a server icon in its health's colour — a
 * crashed server when it is offline — its name and health, how its URLs stand,
 * and the projects it runs. A card opens its server. One server fills the row;
 * more share it, none narrower than 18rem.
 */
function ServerTiles({ servers }: { servers: MonitoringOverview['serverCards'] }) {
  const { t, locale } = useI18n();

  return (
    <Card padded={false} className="mb-5">
      <div className="p-5 pb-0">
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              {t('monitoring.servers.title')}
              {servers.length > 0 && <Badge className="tabular">{formatNumber(servers.length, locale)}</Badge>}
            </span>
          }
          action={
            <Link to="/monitoring/servers" className="text-body-sm text-muted hover:text-[var(--text-primary)]">
              {t('monitoring.overview.allServers')}
            </Link>
          }
        />
      </div>
      {servers.length === 0 ? (
        <EmptyState icon={<Server className="h-8 w-8" />} title={t('monitoring.servers.empty')} hint={t('monitoring.servers.emptyHint')} />
      ) : (
        <ul className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,18rem),1fr))] gap-4 px-5 pb-5">
          {servers.map((server) => (
            <li key={server.id} className="min-w-0">
              <Link
                to={`/monitoring/servers/${server.id}`}
                className={clsx(
                  'group flex h-full flex-col gap-4 rounded-lg border p-4',
                  'transition-[box-shadow,border-color,transform] duration-fast ease-precision motion-safe:hover:-translate-y-0.5',
                  CARD_TONE[server.health],
                )}
              >
                <span className="flex items-start gap-3">
                  <span
                    className={clsx('grid h-11 w-11 shrink-0 place-items-center rounded-md ring-1 ring-inset', ICON_TONE[server.health])}
                    aria-hidden
                  >
                    {server.health === 'OFFLINE' ? <ServerCrash className="h-5 w-5" /> : <Server className="h-5 w-5" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex min-w-0">
                      {/* dir="auto": a Latin name in Arabic loses its end to the ellipsis, not its start. */}
                      <span dir="auto" className="min-w-0 truncate text-title text-[var(--text-primary)]">
                        {server.name}
                      </span>
                    </span>
                    <span className="mt-1 flex">
                      <Badge tone={healthTone(server.health)} dot>
                        {t(healthLabel(server.health))}
                      </Badge>
                    </span>
                  </span>
                  {/* The icon is mirrored in Arabic, so the nudge on hover is a wrapper's. */}
                  <span
                    className="mt-0.5 shrink-0 text-[var(--text-tertiary)] transition-[color,transform] duration-fast ease-precision group-hover:text-primary-600 motion-safe:group-hover:translate-x-0.5 motion-safe:rtl:group-hover:-translate-x-0.5 dark:group-hover:text-primary-400"
                    aria-hidden
                  >
                    <ChevronRight className="h-4 w-4 flip-rtl" />
                  </span>
                </span>

                <span className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-2 border-t hairline pt-3">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <Activity className="h-4 w-4 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
                    <span className="sr-only">{t('monitoring.server.urls')}:</span>
                    <UrlCountsText counts={server.urlCounts} />
                  </span>
                  {/* At the end even when it wraps under the URLs. */}
                  <span className="ms-auto flex flex-wrap items-center justify-end gap-1.5">
                    <Briefcase className="h-3.5 w-3.5 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
                    <span className="sr-only">{t('monitoring.server.projects')}:</span>
                    {server.projects.length === 0 ? (
                      <Badge>{t('monitoring.server.general')}</Badge>
                    ) : (
                      server.projects.map((code) => (
                        <Badge key={code} tone="brand">
                          {code}
                        </Badge>
                      ))
                    )}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
